import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import ClientsPage from "@/pages/ClientsPage";
import WorkshopsPage from "@/pages/WorkshopsPage";
import ProposalsPage from "@/pages/ProposalsPage";
import AIAssistantPage from "@/pages/AIAssistantPage";
import CCSFrameworkPage from "@/pages/CCSFrameworkPage";
import NotificationsPage from "@/pages/NotificationsPage";
import FinancePage from "@/pages/FinancePage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import PublicDiscoveryPage from "@/pages/PublicDiscoveryPage";
import ReflectionPage from "@/pages/ReflectionPage";
import FeedbackPage from "@/pages/FeedbackPage";
import TestimonialWallPage from "@/pages/TestimonialWallPage";
import FacilitatorPage from "@/pages/FacilitatorPage";
import MarketingPage from "@/pages/MarketingPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import HomePage from "@/pages/HomePage";
import SpotlightPage from "@/pages/SpotlightPage";
import SpotlightArticlePage from "@/pages/SpotlightArticlePage";
import SpotlightAdminPage from "@/pages/SpotlightAdminPage";
import InsightsPage from "@/pages/InsightsPage";
import InsightArticlePage from "@/pages/InsightArticlePage";
import InsightsAdminPage from "@/pages/InsightsAdminPage";
import SiteContentAdminPage from "@/pages/SiteContentAdminPage";
import CCSLeaderPage from "@/pages/CCSLeaderPage";
import LegalPage from "@/pages/LegalPage";
import { trackPageview } from "@/lib/track";

const ProtectedRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF5]">
        <div className="text-[#6B5944] font-display">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell><Outlet /></AppShell>;
};

// Fires an anonymous pageview beacon on every public-route change. Server rejects /app/*.
const PageviewTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageview(pathname);
  }, [pathname]);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster richColors position="top-right" toastOptions={{ style: { fontFamily: "'Manrope', sans-serif" } }} />
          <PageviewTracker />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/book" element={<PublicBookingPage />} />
            <Route path="/workshops/the-ccs-leader" element={<CCSLeaderPage />} />
            <Route path="/discover" element={<PublicDiscoveryPage />} />
            <Route path="/reflect/:workshopId/:sessionNumber" element={<ReflectionPage />} />
            <Route path="/feedback/:workshopId" element={<FeedbackPage />} />
            <Route path="/testimonials" element={<TestimonialWallPage />} />
            <Route path="/spotlight" element={<SpotlightPage />} />
            <Route path="/spotlight/:spotlightId" element={<SpotlightArticlePage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:insightId" element={<InsightArticlePage />} />
            <Route path="/privacy-policy" element={<LegalPage page="privacy" />} />
            <Route path="/terms-of-use" element={<LegalPage page="terms" />} />
            <Route path="/intellectual-property" element={<LegalPage page="ip" />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/app/dashboard" element={<DashboardPage />} />
              <Route path="/app/leads" element={<LeadsPage />} />
              <Route path="/app/clients" element={<ClientsPage />} />
              <Route path="/app/workshops" element={<WorkshopsPage />} />
              <Route path="/app/proposals" element={<ProposalsPage />} />
              <Route path="/app/ai" element={<AIAssistantPage />} />
              <Route path="/app/ccs-framework" element={<CCSFrameworkPage />} />
              <Route path="/app/notifications" element={<NotificationsPage />} />
              <Route path="/app/finance" element={<FinancePage />} />
              <Route path="/app/facilitator" element={<FacilitatorPage />} />
              <Route path="/app/marketing" element={<MarketingPage />} />
              <Route path="/app/analytics" element={<AnalyticsPage />} />
              <Route path="/app/spotlight" element={<SpotlightAdminPage />} />
              <Route path="/app/insights" element={<InsightsAdminPage />} />
              <Route path="/app/site-content" element={<SiteContentAdminPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
